import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ReactSortable } from "react-sortablejs";
import { FormInput, registerObject } from "go-form";
import { ReactComponent as SortableHandleSvg } from "go-component/images/svg/sortable-handle.svg";
import { LimitComponentVisibility } from "go-core/components/LimitComponentVisibility";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { CustomValidationError } from "go-form/services/types";
import { UpsellApi, UpsellEntityApi } from "../../../../../../../../../../../services/Api/Organization/types";
import MenuGridPageItemModal from "../../../../Menus/components/MenuPageItemModal/MenuPageItemModal";

interface Props {
	form: UseFormReturn<UpsellApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<UpsellApi>[]>>;
	isVisible: boolean;
	setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const UpsellFormEntitiesTable = ({ form, isVisible, setIsVisible }: Props): JSX.Element => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		control,
		setValue,
		register,
		watch,
	} = form;
	const { fields, remove, append, replace } = useFieldArray({
		control,
		name: "entities",
		keyName: "key",
	});
	const [entitiesTableVisible] = useState(fields.length > 0);
	const [showModal, setShowModal] = useState<boolean>(false);
	const isMobile = useWindowSize().isMobile;
	const isSortingRef = useRef(false);

	const handleSave = (item: Record<string, any>) => {
		setShowModal(false);
		append({
			entity_id: item.context_id,
			type: item.context_type,
			name: item.entity.name,
			position: watch("entities").length,
		});
	};

	useEffect(() => {
		fields.forEach((field: UpsellEntityApi, index: number) => {
			registerObject(register, `entities.${index}`, ["entity_id", "position", "type"]);
			setValue(`entities.${index}`, field);
		});
	}, [fields]);

	const updatePositions = (entities: UpsellEntityApi[]) => {
		if (!isSortingRef.current) return;

		isSortingRef.current = false;
		entities.forEach((entity, index) => {
			entity.position = index;
		});
		replace(entities);
	};

	return (
		<LimitComponentVisibility
			classNames="mb-3"
			defaultVisible={entitiesTableVisible}
			handleChange={(val) => val === false && setValue("entities", [], { shouldDirty: true })}
			id="upsell_entities"
			helpText={t("modules.upsell.field.items.help_text")}
			legendText={t("modules.upsell.field.items.title")}
			isVisibleControl={isVisible}
			setIsVisibleControl={setIsVisible}
			unlimitedContent={
				<>
					<div className={`${isMobile ? "table-responsive" : ""}`}>
						<table className="table table-form">
							<thead>
								<tr>
									<th className="action" />
									<th>{`${t("common.word.item_group")} / ${t("common.word.variant")}`}</th>
									<th className="action" />
								</tr>
							</thead>
							<ReactSortable
								handle=".sortable-handler"
								list={fields}
								tag="tbody"
								onUpdate={() => (isSortingRef.current = true)}
								setList={(newFields) => updatePositions(newFields)}
							>
								{fields.map((item: UpsellEntityApi, index: number) => {
									return (
										<tr key={fields[index].key}>
											<td className="action">
												<SortableHandleSvg className="sortable-handler" />
											</td>
											<td>
												<FormInput
													type="hidden"
													errors={errors}
													defaultValue={index}
													name={`entities.${index}.position`}
													register={register}
												/>
												<FormInput
													type="hidden"
													register={register}
													errors={errors}
													name={`entities.${index}.entity_id`}
												/>
												{item.name ?? ""}
											</td>
											<td className="action">
												<RemoveSVG className="icon" onClick={() => remove(index)} />
											</td>
										</tr>
									);
								})}
							</ReactSortable>
						</table>
					</div>
					<Button variant="add" type="button" onClick={() => setShowModal(true)}>
						{`+ ${t("lib:common.action.add")}`}
					</Button>
					{showModal && (
						<MenuGridPageItemModal
							selectedValues={fields.map((field: UpsellEntityApi) => {
								return {
									id: field.entity_id,
									type: field.type,
								};
							})}
							contextTypes={["ITEM", "ITEM_GROUP"]}
							handleClose={() => setShowModal(false)}
							handleSave={handleSave}
						/>
					)}
				</>
			}
		/>
	);
};
export default UpsellFormEntitiesTable;
